import React, { useEffect, useMemo, useState } from "react";

import {
  BasketCombinedSortType,
  BasketSortDirection,
  BasketsSort,
  useBasketsDelete,
  useDimensions,
} from "@keepeek/commons";
import {
  BasketDelete,
  KButton,
  SanitizeHtml,
  Sort,
  withSuspense,
} from "@keepeek/refront-components";
import { Box, TextField } from "@mui/material";
import { debounce } from "lodash";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";

import { KIcon } from "../../../../components/common/KIcon";
import { BasketsWidget as BasketsWidgetModel } from "../../../../models/configuration/components/basketsWidget";
import { selectedBasketsWidgetState } from "../atoms";
import BasketsWidgetActionsSelect from "./BasketsWidgetActionsSelect";
import StyledBasketsWidgetActions, { classes } from "./index.styled";

const BasketsWidgetActions = function ({
  widgetId,
  order,
  search,
  size,
  page,
  setPage,
  sort,
  setSort,
  queryFilter,
  setQueryFilter,
}: {
  widgetId: string;
  page: number;
  setPage: (value: number) => void;
  size: number;
  sort: BasketCombinedSortType;
  setSort: (sort: BasketCombinedSortType) => void;
  queryFilter: string;
  setQueryFilter: (q: string) => void;
} & Pick<BasketsWidgetModel, "order" | "search">) {
  const { t } = useTranslation();
  const [selectedBaskets, setSelectedBaskets] = useRecoilState(
    selectedBasketsWidgetState(widgetId),
  );
  const {
    open: openDelete,
    close: closeDelete,
    deleteBaskets,
    setOpen: setOpenDelete,
  } = useBasketsDelete(selectedBaskets);

  const [valueQ, setValueQ] = useState(queryFilter);

  // Utilisez useMemo pour mémoriser la fonction debouncée
  const debounced = useMemo(
    () =>
      debounce((value) => {
        setPage(1);
        setQueryFilter(value);
      }, 300),
    [setPage, setQueryFilter],
  );

  useEffect(() => {
    setSelectedBaskets([]);
    debounced(valueQ);
    // Assurez-vous d'annuler le debounce si le composant est démonté
    return () => {
      debounced.cancel();
    };
  }, [debounced, setSelectedBaskets, valueQ]);

  const basketSortList = Object.values(BasketsSort)
    .map((s) => {
      return Object.values(BasketSortDirection)
        .reverse()
        .map((d) => {
          return {
            label: (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <SanitizeHtml
                  html={`${t(`sort-list.generic.${d.toUpperCase()}`, {
                    field: t(`basketWidget.actions.sort.${s}.label`),
                  })}`}
                />
                {BasketSortDirection.ASC === d ? (
                  <KIcon className={"fa-regular fa-arrow-up fa-lg"} />
                ) : (
                  <KIcon className={"fa-regular fa-arrow-down fa-lg"} />
                )}
              </Box>
            ),
            value: `${s} ${d}`,
          };
        });
    })
    .flat();

  const { ref, dimensions } = useDimensions({});
  const [barWidth, setBarWith] = useState(dimensions.width);
  useEffect(() => {
    setBarWith(dimensions.width);
  }, [ref, dimensions.width]);

  return (
    <StyledBasketsWidgetActions ref={ref} barWidth={barWidth}>
      <Box className={classes.actionsContainer} gap={1}>
        <BasketsWidgetActionsSelect
          widgetId={widgetId}
          queryFilter={queryFilter}
          sizeParam={size}
          pageParam={page}
        />
        <Box>
          {openDelete && (
            <BasketDelete
              open={openDelete}
              close={closeDelete}
              currentBasketTitle={""}
              deleteBasket={async () => {
                await deleteBaskets();
                setSelectedBaskets([]);
              }}
              numberOfBaskets={selectedBaskets.length || 0}
            />
          )}
          <KButton
            disabled={selectedBaskets.length === 0}
            onClick={() => {
              setOpenDelete(true);
            }}
            buttonName={"deleteBaskets"}
            startIcon={<KIcon className={"fa-solid fa-trash"} />}
          >
            {t("common:action.delete")}{" "}
            {selectedBaskets.length !== 0 ? `(${selectedBaskets.length})` : ""}
          </KButton>
        </Box>
      </Box>
      {(search?.show ?? true) && (
        <Box className={classes.searchContainer}>
          <TextField
            placeholder={t("basketWidget.actions.search.placeholder.label")}
            size={"small"}
            fullWidth
            value={valueQ}
            onChange={(e) => setValueQ(e.currentTarget.value)}
            InputProps={{
              endAdornment: <KIcon className="fa-solid fa-magnifying-glass" />,
            }}
          />
        </Box>
      )}
      {(order?.show ?? true) && (
        <Box className={classes.orderContainer}>
          <Sort
            sortList={basketSortList}
            defaultSortValue={sort}
            onSortChange={(value) => setSort(value as BasketCombinedSortType)}
            sx={{
              "& .MuiSelect-icon": {
                display: "none",
              },
              "& .MuiListItemIcon-root": {
                minWidth: "auto",
              },
              "&& .MuiInput-input": {
                paddingRight: "0!important",
              },
              "& .MuiSelect-select": {
                fontSize: ".8rem",
              },
            }}
          />
        </Box>
      )}
    </StyledBasketsWidgetActions>
  );
};

export default withSuspense(BasketsWidgetActions);
